import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth, SignInButton } from '@clerk/react';
import theme from '../../theme';
import PageHeader from '../../components/PageHeader';
import { fetchCourse, enrollInCourse } from '../../lib/lmsApi';

const CSS = `
  .syllabus-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin: 24px 0 32px;
  }
  .syllabus-item {
    display: flex;
    align-items: baseline;
    gap: 16px;
    padding: 14px 16px;
    background: ${theme.bgCard};
    border-radius: 3px;
    text-decoration: none;
  }
  .syllabus-num {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12px;
    color: ${theme.gold};
    min-width: 18px;
  }
  .syllabus-title {
    font-family: 'EB Garamond', serif;
    font-size: 17px;
    color: ${theme.textPrimary};
  }
  .syllabus-mins {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 11px;
    color: ${theme.textMuted};
    margin-left: auto;
  }
  /* Reserved whether or not the tick is drawn, so the titles stay on one
     vertical line instead of jumping as lessons are completed. */
  .syllabus-done {
    font-size: 13px;
    color: ${theme.success};
    min-width: 14px;
  }
  .syllabus-progress {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12px;
    color: ${theme.textMuted};
    /* Clears the Start Course button above it. A negative top margin here
       pulled the line up underneath that button and overlapped it. */
    margin: 20px 0 0;
  }
  /* The list carries a 24px top margin for the case where no progress line is
     rendered; when one is, that gap is already spent. */
  .syllabus-progress + .syllabus-list {
    margin-top: 12px;
  }
`;

function CourseDetailPage() {
  const { courseSlug } = useParams();
  const navigate = useNavigate();
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [course, setCourse] = useState(null);
  const [error, setError] = useState(false);
  const [starting, setStarting] = useState(false);

  // Refetched when sign-in state settles, not just when the slug changes: the
  // response depends on whether there is a session (bodies, and the caller's
  // own progress), and on first paint Clerk has usually not resolved yet.
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!isLoaded) return;
    fetchCourse(courseSlug, isSignedIn ? getToken : null)
      .then((c) => {
        setCourse(c);
        document.title = `${c.title} - Austrian Process`;
      })
      .catch(() => setError(true));
  }, [courseSlug, isLoaded, isSignedIn, getToken]);

  // Which lessons this student has finished. Empty for anyone signed out, so
  // the syllabus renders exactly as before for them.
  const completed = new Set(course?.progress?.completedLessonIds || []);
  const done = course?.lessons?.filter((l) => completed.has(l._id)).length || 0;

  const handleStart = async () => {
    if (!course?.lessons?.length) return;
    setStarting(true);
    try {
      await enrollInCourse(getToken, course.slug);
      navigate(`/${course.slug}/${course.lessons[0].slug}`);
    } catch {
      setStarting(false);
    }
  };

  if (error) {
    return (
      <div className="page-container">
        <p style={{ color: theme.error }}>Couldn't load this course.</p>
      </div>
    );
  }
  if (!course) {
    return (
      <div className="page-container">
        <p style={{ color: theme.textMuted }}>Loading...</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      <style>{CSS}</style>
      <PageHeader title={course.title} description={course.description} />

      {isLoaded && (isSignedIn ? (
        <button
          type="button"
          className="btn-primary"
          onClick={handleStart}
          disabled={starting}
        >
          {starting ? 'Starting...' : 'Start Course'}
        </button>
      ) : (
        <SignInButton mode="modal">
          <button type="button" className="btn-primary">Sign In to Start</button>
        </SignInButton>
      ))}

      {done > 0 && (
        <p className="syllabus-progress">{done} of {course.lessons.length} lessons complete</p>
      )}

      <div className="syllabus-list">
        {course.lessons.map((lesson) => (
          <Link key={lesson.slug} to={`/${course.slug}/${lesson.slug}`} className="syllabus-item">
            <span className="syllabus-num">{String(lesson.order).padStart(2, '0')}</span>
            <span className="syllabus-done">{completed.has(lesson._id) ? '\u2713' : ''}</span>
            <span className="syllabus-title">{lesson.title}</span>
            <span className="syllabus-mins">
              {lesson.estimatedMinutes} min
              {lesson.readings?.length
                ? ` · ${lesson.readings.length} reading${lesson.readings.length === 1 ? '' : 's'}`
                : ''}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default CourseDetailPage;
